import { useEffect, useMemo, useState } from 'react';
import useSWRImmutable from 'swr/immutable';

import {
  Avatar,
  createUserAvatar,
  deleteAvatarByUserId,
  updateUserAvatar,
} from '@/api/cms/avatars';
import { getActiveAvatar } from '@/helpers/avatar';
import { saveUserData } from '@/helpers/userData';
import { fetcherCMS } from '@/modules/api';
import { createCMSQuery } from '@/modules/cms';
import { useCommonStore } from '@/store';

const DEFAULT_AVATAR = [
  {
    avatarId: null,
    avatarImg: null,
    avatarCmsFieldId: null,
    id: null,
  },
];

export const useCMSAvatar = () => {
  const user = useCommonStore.use.user();
  const [isMutating, setIsMutating] = useState(false);

  const query = useMemo(() => {
    return createCMSQuery({
      filters: { user_id: { $eq: user?.id } },
    });
  }, [user?.id]);

  const swrKey = user?.id ? `/avatars?${query}` : null;

  const {
    data = DEFAULT_AVATAR,
    error,
    isLoading,
    mutate: mutateAvatar,
  } = useSWRImmutable<Avatar[]>(swrKey, fetcherCMS);

  const { avatarId, avatarImg, avatarCmsFieldId } = useMemo(() => {
    const userAvatar = data && data.length > 0 ? data[0] : DEFAULT_AVATAR[0];
    const avatarId = userAvatar.avatarId;

    return {
      avatarId,
      avatarImg: getActiveAvatar(avatarId).avatar,
      avatarCmsFieldId: userAvatar.id,
    };
  }, [data, user]);

  useEffect(() => {
    const avatarId = data?.[0]?.avatarId;
    if (avatarId !== null) {
      saveUserData({ avatarId });
    }
  }, [data]);

  const changeAvatar = async (newAvatarId: number) => {
    if (!user?.id) return;
    setIsMutating(true);

    const payload = { userId: user.id, avatarId: newAvatarId };

    if (avatarCmsFieldId) {
      await updateUserAvatar(avatarCmsFieldId, payload);
    } else {
      await createUserAvatar(payload);
    }

    await mutateAvatar();
    saveUserData({ avatarId: newAvatarId });
    setIsMutating(false);
  };

  const deleteAvatar = async () => {
    if (!avatarCmsFieldId) return;
    setIsMutating(true);

    await deleteAvatarByUserId(avatarCmsFieldId);
    await mutateAvatar();
    saveUserData({ avatarId: null });

    setIsMutating(false);
  };

  return {
    avatarId,
    avatarImg,
    avatarCmsFieldId,
    isLoading,
    isError: !!error,
    mutateAvatar,
    changeAvatar,
    deleteAvatar,
    isMutating,
  };
};
